// M7 Freeze — Squid Game red light / green light.
// Spec: /microgames/07-freeze.md
// Move freely when GREEN. Stay still when RED. Survive 15 seconds.

import type {
  GameAudio,
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
} from '@pose-game/core';

const DURATION_SEC = 22;
const MIN_PHASE_SEC = 2.0;
const MAX_PHASE_SEC = 4.5;
const RED_GRACE_SEC = 0.6;
const MOTION_THRESHOLD = 0.022;
const ANIM_DURATION_SEC = 0.45;

type Phase = 'green' | 'red';

interface State {
  ctx: GameContext | null;
  audio: GameAudio | null;
  rng: () => number;
  elapsed: number;
  phase: Phase;
  phaseElapsed: number;
  phaseDuration: number;
  animElapsed: number; // since last phase change (for bouncy text scale-in)
  motionDelta: number;
  failed: boolean;
  failedAt: number;
  finished: boolean;
}

function createInitialState(): State {
  return {
    ctx: null,
    audio: null,
    rng: Math.random,
    elapsed: 0,
    phase: 'green',
    phaseElapsed: 0,
    phaseDuration: MIN_PHASE_SEC,
    animElapsed: 0,
    motionDelta: 0,
    failed: false,
    failedAt: 0,
    finished: false,
  };
}

function pickPhaseDuration(rng: () => number): number {
  return MIN_PHASE_SEC + rng() * (MAX_PHASE_SEC - MIN_PHASE_SEC);
}

// Ease-out elastic-ish bounce, settles at 1 at t = 1.
function bouncyScale(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  // Damped oscillation: 1 - e^(-6t) * cos(4πt)
  return 1 - Math.exp(-6 * t) * Math.cos(4 * Math.PI * t);
}

function switchPhase(state: State): void {
  state.phase = state.phase === 'green' ? 'red' : 'green';
  state.phaseElapsed = 0;
  state.phaseDuration = pickPhaseDuration(state.rng);
  state.animElapsed = 0;
  if (state.audio != null) {
    if (state.phase === 'red') {
      state.audio.thud();
    } else {
      state.audio.beep();
    }
  }
}

function tickState(state: State, dt: number, frame: InputFrame): void {
  if (state.finished) return;

  state.elapsed += dt;
  state.phaseElapsed += dt;
  state.animElapsed += dt;
  state.motionDelta = frame.pose.motionDelta;

  // Win check first — survive duration without failing.
  if (state.elapsed >= DURATION_SEC) {
    state.finished = true;
    if (!state.failed && state.audio != null) state.audio.win();
    return;
  }

  // Phase switch.
  if (state.phaseElapsed >= state.phaseDuration) {
    switchPhase(state);
  }

  // Violation check (only during RED, after grace).
  if (
    !state.failed &&
    state.phase === 'red' &&
    state.phaseElapsed >= RED_GRACE_SEC
  ) {
    const movedBody = frame.pose.visible && frame.pose.motionDelta > MOTION_THRESHOLD;
    const movedMouth = frame.face.visible && frame.face.biteEdge;
    if (movedBody || movedMouth) {
      state.failed = true;
      state.failedAt = state.elapsed;
      state.finished = true;
      if (state.audio != null) state.audio.lose();
    }
  }
}

function renderState(state: State, g: CanvasRenderingContext2D): void {
  if (state.ctx == null) return;
  const { canvas } = state.ctx;
  const w = canvas.width;
  const h = canvas.height;

  // Background flash.
  g.fillStyle = state.phase === 'green' ? '#1e6b4f' : '#8a1a25';
  g.fillRect(0, 0, w, h);

  // Center label with bouncy scale-in on each phase change.
  const animT = Math.min(1, state.animElapsed / ANIM_DURATION_SEC);
  const scale = bouncyScale(animT);
  const label = state.phase === 'green' ? 'GO!' : 'FREEZE!';
  const baseSize = Math.round(h * 0.18);
  const cx = w / 2;
  const cy = h / 2;
  g.save();
  g.translate(cx, cy);
  g.scale(scale, scale);
  g.fillStyle = '#ffffff';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `bold ${baseSize}px system-ui, sans-serif`;
  // Subtle stroke for legibility on either background.
  g.lineWidth = Math.max(2, baseSize * 0.05);
  g.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  g.strokeText(label, 0, 0);
  g.fillText(label, 0, 0);
  g.restore();

  // HUD top: elapsed bar (counts up from 0 → 15s).
  const barMargin = Math.round(w * 0.04);
  const barWidth = w - barMargin * 2;
  const barHeight = Math.max(8, Math.round(h * 0.018));
  const barX = barMargin;
  const barY = Math.round(h * 0.04);
  g.fillStyle = 'rgba(0, 0, 0, 0.35)';
  g.fillRect(barX, barY, barWidth, barHeight);
  const elapsedFrac = Math.min(1, state.elapsed / DURATION_SEC);
  g.fillStyle = '#ffffff';
  g.fillRect(barX, barY, barWidth * elapsedFrac, barHeight);
  g.fillStyle = 'rgba(255, 255, 255, 0.85)';
  g.font = `${Math.round(h * 0.025)}px system-ui, sans-serif`;
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText(
    `${state.elapsed.toFixed(1)}s / ${DURATION_SEC}s`,
    barX,
    barY + barHeight + Math.round(h * 0.008),
  );

  // Motion meter at bottom: fills proportional to motionDelta / (2 * threshold).
  // Visual full = 2x threshold so the player can see headroom.
  const meterHeight = Math.max(10, Math.round(h * 0.025));
  const meterY = h - meterHeight - Math.round(h * 0.04);
  const meterWidth = barWidth;
  const meterX = barX;
  g.fillStyle = 'rgba(0, 0, 0, 0.4)';
  g.fillRect(meterX, meterY, meterWidth, meterHeight);
  const meterFull = MOTION_THRESHOLD * 2;
  const meterFrac = Math.max(0, Math.min(1, state.motionDelta / meterFull));
  // Color: stays calm green when low, glows red as it approaches threshold.
  const dangerFrac = Math.max(
    0,
    Math.min(1, state.motionDelta / MOTION_THRESHOLD),
  );
  const r = Math.round(80 + 175 * dangerFrac);
  const gComp = Math.round(200 * (1 - dangerFrac) + 40 * dangerFrac);
  const b = 60;
  g.fillStyle = `rgb(${r}, ${gComp}, ${b})`;
  g.fillRect(meterX, meterY, meterWidth * meterFrac, meterHeight);
  // Threshold tick at 50% of the meter (since meterFull = 2 * threshold).
  const tickX = meterX + meterWidth * 0.5;
  g.fillStyle = '#ffffff';
  g.fillRect(tickX - 1, meterY - 2, 2, meterHeight + 4);
  g.fillStyle = 'rgba(255, 255, 255, 0.85)';
  g.font = `${Math.round(h * 0.022)}px system-ui, sans-serif`;
  g.textAlign = 'left';
  g.textBaseline = 'bottom';
  g.fillText('motion', meterX, meterY - Math.round(h * 0.006));
}

export const freeze: Microgame = (() => {
  const state = createInitialState();
  return {
    name: '07-freeze',
    durationSec: DURATION_SEC,
    requires: { pose: true, face: true },
    init(c: GameContext): void {
      state.ctx = c;
      state.audio = c.audio;
      state.rng = c.rng ?? Math.random;
      state.elapsed = 0;
      // Always start on GREEN so the player gets a clean intro.
      state.phase = 'green';
      state.phaseElapsed = 0;
      state.phaseDuration = pickPhaseDuration(state.rng);
      state.animElapsed = 0;
      state.motionDelta = 0;
      state.failed = false;
      state.failedAt = 0;
      state.finished = false;
    },
    tick(dt: number, frame: InputFrame): void {
      tickState(state, dt, frame);
    },
    render(g: CanvasRenderingContext2D): void {
      renderState(state, g);
    },
    end(): MicrogameResult {
      const won = !state.failed;
      return {
        microgame: '07-freeze',
        won,
        score: won ? 1 : 0,
        detail: { failedAt: state.failedAt },
      };
    },
  };
})();
